import { MapPin, ChevronRight, Plus, Minus } from 'lucide-react';
import { useState } from 'react';
import { cvData } from '../../data/cv';
import { useInView } from '../../hooks/useInView';

const PREVIEW_COUNT = 4;

const JobCard = ({ job, idx, featured }: { job: typeof cvData.experience[0]; idx: number; featured: boolean }) => {
  const { ref, inView } = useInView();
  const [expanded, setExpanded] = useState(false);
  const hasMore = job.responsibilities.length > PREVIEW_COUNT;
  const visible = expanded ? job.responsibilities : job.responsibilities.slice(0, PREVIEW_COUNT);

  return (
    <div
      ref={ref}
      className={`relative p-6 sm:p-7 md:p-8 rounded-2xl shadow-card transition-all duration-500 hover:-translate-y-1 hover:shadow-elevated h-full flex flex-col ${
        featured
          ? 'bg-primary text-primary-foreground lg:-translate-y-4 shadow-hero'
          : 'bg-card text-foreground border-b-4 border-accent border-x border-t border-border'
      } ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{ transitionDelay: `${idx * 80}ms` }}
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.25em] block mb-3 text-accent">
        {job.startDate} — {job.endDate}
      </span>
      <h3
        className={`text-xl md:text-2xl font-bold mb-2 leading-snug ${featured ? '' : 'text-foreground'}`}
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {job.title}
      </h3>
      <p className={`text-sm font-semibold mb-4 ${featured ? 'text-accent' : 'text-primary'}`}>
        {job.company}
      </p>
      <p className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wider mb-5 ${featured ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
        <MapPin size={11} />
        {job.location}
      </p>
      <ul className="space-y-2.5 flex-1">
        {visible.map((resp, respIdx) => (
          <li
            key={respIdx}
            className={`flex items-start gap-2.5 text-sm leading-relaxed ${
              featured ? 'text-primary-foreground/80' : 'text-foreground/70'
            }`}
          >
            <ChevronRight size={13} className="flex-shrink-0 mt-1 text-accent" />
            <span>{resp}</span>
          </li>
        ))}
      </ul>
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`mt-5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] w-fit transition-colors ${
            featured ? 'text-accent hover:text-accent/80' : 'text-primary hover:text-accent'
          }`}
          aria-expanded={expanded}
        >
          {expanded ? <Minus size={12} /> : <Plus size={12} />}
          {expanded ? 'Show less' : `Show all ${job.responsibilities.length} duties`}
        </button>
      )}
    </div>
  );
};

export const Experience = () => {
  const { ref: headRef, inView: headIn } = useInView();

  return (
    <section id="experience" className="py-20 sm:py-24 md:py-28 px-4 sm:px-6 md:px-12 lg:px-20 section-alt relative">
      <div className="max-w-7xl mx-auto">

        <div ref={headRef} className={`flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6 mb-10 sm:mb-12 md:mb-16 transition-all duration-700 ${headIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="shrink-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-accent mb-3">Career</p>
            <h2
              className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-foreground whitespace-nowrap"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Experience
            </h2>
          </div>
          <div className="hidden sm:block h-px w-full bg-border mb-3" />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {cvData.experience.map((job, idx) => (
            <JobCard key={idx} job={job} idx={idx} featured={idx % 3 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
};
